import * as yup from 'yup';
import {
  fieldLengthLimit,
  operatoryNumberLimit,
  patientIdLengthLimit,
  treatmentCodeLengthLimit,
  longFieldLengthLimit,
  userNameLengthLimit,
  validation,
  validationErrors
} from './constants';

const nameValidation = yup.string()
    .max(fieldLengthLimit, validationErrors.maxLength(fieldLengthLimit))
    .matches(validation.userName, validationErrors.userName)
    .required(validationErrors.required);

const userNameValidation = yup.string()
    .max(userNameLengthLimit, validationErrors.maxLength(userNameLengthLimit))
    .matches(validation.userName, validationErrors.userName)
    .required(validationErrors.required);

const emailValidation = yup.string()
    .email(validationErrors.email)
    .max(fieldLengthLimit, validationErrors.maxLength(fieldLengthLimit))
    .required(validationErrors.required);

const alphaNumericValidation = yup.string()
    .max(fieldLengthLimit, validationErrors.maxLength(fieldLengthLimit))
    .matches(validation.alphaNumeric, validationErrors.alphaNumeric)
    .required(validationErrors.required);

const numericValidation = yup.string()
    .matches(validation.numeric, validationErrors.numeric)
    .required(validationErrors.required);

const phoneNumberValidation = yup.string()
    .matches(validation.phoneNumber, validationErrors.phoneNumber);

const patientIdLengthValidation = yup.string()
    .max(patientIdLengthLimit, validationErrors
        .maxLength(patientIdLengthLimit));

const fieldLengthValidation = yup.string()
    .max(fieldLengthLimit, validationErrors
        .maxLength(fieldLengthLimit));

const textFieldValidation = yup.string()
    .max(fieldLengthLimit, validationErrors.maxLength(fieldLengthLimit))
    .matches(validation.textField, validationErrors.textField)
    .required(validationErrors.required);

const longTextFieldValidation = yup.string()
    .max(longFieldLengthLimit, validationErrors.maxLength(longFieldLengthLimit))
    .matches(validation.textField, validationErrors.textField)
    .required(validationErrors.required);

const requiredValidation = yup.string().required(validationErrors.required);

const requiredNullableValidation =
  yup.string().required(validationErrors.required).nullable();

const alertPercentsValidation = yup.string()
    .matches(validation.percent, validationErrors.percent)
    .required(validationErrors.shortRequired);

const processTimeHoursValidation = yup.string()
    .matches(validation.hours, validationErrors.hours)
    .required(validationErrors.shortRequired);

const processTimeWithoutHoursValidation = yup.string()
    .matches(validation.hoursWithMinutes, validationErrors.hoursWithMinutes)
    .required(validationErrors.shortRequired);

const processMinutesValidation = yup.string()
    .matches(validation.minutesWithHours, validationErrors.minutesWithHours)
    .required(validationErrors.shortRequired);

const processTimeMinutesWithoutHoursValidation = yup.string()
    .matches(
        validation.minutesWithoutHours,
        validationErrors.minutesWithoutHours
    )
    .required(validationErrors.shortRequired);

const alertMinutesValidation = yup.string()
    .matches(validation.minutes, validationErrors.minutes)
    .required(validationErrors.shortRequired);

const alertSecondsValidation = yup.string()
    .matches(validation.seconds, validationErrors.seconds)
    .required(validationErrors.shortRequired);

const groupsSelectValidation = yup.array()
    .min(1, validationErrors.required);

const tenantSelectValidation = yup.number()
    .required(validationErrors.required).nullable();

export const addUserValidationSchema = yup.object().shape({
  email: emailValidation,
  groups: groupsSelectValidation
});

export const addSystemUserValidationSchema = yup.object().shape({
  email: emailValidation,
  tenantId: tenantSelectValidation,
  groups: groupsSelectValidation
});

export const editUserValidationSchema = yup.object().shape({
  firstName: userNameValidation,
  lastName: userNameValidation
});

export const editUserEmailValidationSchema = yup.object().shape({
  email: emailValidation
});

export const patientValidationSchema = yup.object().shape({
  firstName: nameValidation,
  lastName: nameValidation,
  phoneNumber: phoneNumberValidation,
  patientId: patientIdLengthValidation
});

export const editGroupValidationSchema = yup.object().shape({
  name: textFieldValidation
});

export const editTenantValidationSchema = yup.object().shape({
  name: textFieldValidation
});

export const addGroupValidationSchema = yup.object().shape({
  slug: alphaNumericValidation,
  name: textFieldValidation
});

export const addTenantValidationSchema = yup.object().shape({
  slug: alphaNumericValidation,
  name: textFieldValidation,
  industryCode: requiredValidation
});

export const doctorValidationSchema = yup.object().shape({
  lastName: nameValidation,
  firstName: nameValidation,
  externalId: fieldLengthValidation
});

export const operatoryValidationSchema = yup.object().shape({
  desc: longTextFieldValidation,
  number: numericValidation.max(operatoryNumberLimit,
      validationErrors.maxLength(operatoryNumberLimit)).nullable()
});

export const treatmentValidationSchema = yup.object().shape({
  name: yup.string().max(fieldLengthLimit,
      validationErrors.maxLength(fieldLengthLimit))
      .required(validationErrors.required)
});

export const treatmentWithCodeValidationSchema = yup.object().shape({
  name: yup.string().max(longFieldLengthLimit,
      validationErrors.maxLength(longFieldLengthLimit))
      .required(validationErrors.required),
  code: yup.string().max(treatmentCodeLengthLimit,
      validationErrors.maxLength(treatmentCodeLengthLimit))
      .required(validationErrors.required)
});

export const callSheetValidationSchema = yup.object().shape({
  patientFirstName: nameValidation,
  patientLastName: nameValidation,
  patientId: patientIdLengthValidation
});

export const addSurveyValidationSchema = yup.object().shape({
  patientId: numericValidation,
  callSheetId: numericValidation
});

export const startProcessValidationSchema = yup.object().shape({
  patientId: requiredNullableValidation
});

export const absRangeValidationSchema = yup.object().shape({
  minutes: alertMinutesValidation,
  seconds: alertSecondsValidation
});

export const processTimeWithHoursValidationSchema = yup.object().shape({
  hours: processTimeHoursValidation,
  minutes: processMinutesValidation,
  seconds: alertSecondsValidation
});

export const processTimeWithoutHoursValidationSchema = yup.object().shape({
  hours: processTimeWithoutHoursValidation,
  minutes: processTimeMinutesWithoutHoursValidation,
  seconds: alertSecondsValidation
});

export const relRangeValidationSchema = yup.object().shape({
  range: alertPercentsValidation
});

export const reassignTaskValidationSchema = yup.object().shape({
  assigneeId: requiredNullableValidation
});
